import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productData } from '../mock';
import { ShoppingCart, AlertTriangle, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Shop = () => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState('local');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  const handleAddToCart = () => {
    if (!isLoggedIn) {
      toast.error('Please login or register to place an order');
      navigate('/login');
      return;
    }

    if (quantity > productData.currentStock) {
      toast.error(`Only ${productData.currentStock} bottles available!`);
      return;
    }
    setShowCheckoutDialog(true);
  };

  const handleCheckout = async () => {
    if (!isLoggedIn) {
      toast.error('Please login to complete your order');
      navigate('/login');
      return;
    }

    setSubmitting(true);

    try {
      const orderData = {
        items: [
          {
            product_id: productData.id,
            name: productData.name,
            quantity: quantity,
            price: productData.price
          }
        ],
        shipping_method: getShippingOption()?.name || 'Local Pickup',
        shipping_cost: getShippingOption()?.price || 0,
        total_amount: calculateTotal()
      };

      await axios.post(
        `${BACKEND_URL}/api/orders`,
        orderData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast.success('Order placed successfully! Check your email for confirmation.');
      setShowCheckoutDialog(false);
      setQuantity(1);
      
      // Redirect to a thank you page or orders page
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const getShippingOption = () => {
    return productData.shippingOptions.find(opt => opt.id === selectedShipping);
  };

  const calculateTotal = () => {
    const shipping = getShippingOption();
    return (productData.price * quantity) + (shipping ? shipping.price : 0);
  };

  const HeatIndicator = ({ level }) => {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-300">Heat Level:</span>
        <div className="flex gap-1">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className={`w-6 h-6 rounded ${
                i < level
                  ? 'bg-gradient-to-t from-red-600 to-orange-400'
                  : 'bg-gray-700'
              }`}
            />
          ))}
        </div>
        <span className="text-sm font-bold text-orange-400">{level}/10</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            Shop <span className="text-orange-500">Limited Edition</span>
          </h1>
          <p className="text-xl text-gray-400">
            Small batch. Craft quality. Once it's gone, it's gone forever.
          </p>
        </div>
      </section>

      {/* Heat Warning Banner */}
      <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 border-y border-red-600/50 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-center">
          <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
          <p className="text-white font-bold">
            {productData.warning}
          </p>
          <Flame className="w-6 h-6 text-orange-500 flex-shrink-0" />
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images/Video Gallery */}
            <div>
              <div className="relative mb-4 rounded-xl overflow-hidden bg-gray-900">
                {selectedImage === 0 ? (
                  <video 
                    src={productData.images[selectedImage]}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto max-h-[600px] object-contain mx-auto"
                  />
                ) : (
                  <img 
                    src={productData.images[selectedImage]}
                    alt={`${productData.name} view ${selectedImage}`}
                    className="w-full h-auto max-h-[600px] object-contain mx-auto"
                  />
                )}
                <Badge className="absolute top-4 left-4 bg-red-600 text-white font-bold px-3 py-1">
                  LIMITED EDITION
                </Badge>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                {productData.images.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      selectedImage === idx 
                        ? 'border-orange-500 scale-105' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {idx === 0 ? (
                      <video 
                        src={image}
                        muted
                        playsInline
                        className="w-full h-full object-cover aspect-square"
                      />
                    ) : (
                      <img 
                        src={image}
                        alt={`Thumbnail ${idx}`}
                        className="w-full h-full object-cover aspect-square"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              <h2 className="text-4xl font-black mb-2">{productData.name}</h2>
              <p className="text-2xl text-orange-400 font-medium mb-6">{productData.subtitle}</p>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-5xl font-black text-white">${productData.price.toFixed(2)}</span>
                  <span className="text-gray-400">per bottle ({productData.size})</span>
                </div>
                <p className="text-sm text-orange-400">Local pickup price</p>
                <p className="text-sm text-gray-400">Select delivery method below for shipping costs</p>
              </div>

              {/* Stock Indicator */}
              <Card className="bg-green-900/30 border-green-600/50 mb-6">
                <CardContent className="p-4">
                  <div className="text-center">
                    <span className="text-green-400 font-black text-2xl">
                      ✓ STILL IN STOCK
                    </span>
                  </div>
                </CardContent>
              </Card>

              <HeatIndicator level={productData.heatLevel} />

              <div className="my-6 border-t border-gray-700" />

              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-white">Description</h3>
                <p className="text-gray-300 leading-relaxed">{productData.description}</p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-white">Pepper Types</h3>
                <div className="flex flex-wrap gap-2">
                  {productData.pepperTypes.map((pepper, idx) => (
                    <Badge key={idx} variant="outline" className="border-orange-500 text-orange-400 px-3 py-1">
                      {pepper}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-white">Flavor Notes</h3>
                <div className="flex flex-wrap gap-2">
                  {productData.flavorNotes.map((note, idx) => (
                    <Badge key={idx} variant="outline" className="border-gray-600 text-gray-300 px-3 py-1">
                      {note}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-bold mb-3 text-white">Ingredients</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{productData.ingredients}</p>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-bold mb-2 text-gray-300">Quantity</label>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="border-gray-600 text-white hover:bg-gray-800"
                  >
                    -
                  </Button>
                  <span className="text-2xl font-bold w-12 text-center">{quantity}</span>
                  <Button
                    variant="outline"
                    onClick={() => setQuantity(Math.min(productData.currentStock, quantity + 1))}
                    className="border-gray-600 text-white hover:bg-gray-800"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Shipping Options */}
              <div className="mb-6">
                <label className="block text-sm font-bold mb-3 text-gray-300">Delivery Method</label>
                <div className="space-y-3">
                  {productData.shippingOptions.map((option) => (
                    <Card
                      key={option.id}
                      className={`cursor-pointer transition-all duration-300 ${
                        selectedShipping === option.id
                          ? 'bg-orange-900/30 border-orange-500'
                          : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                      }`}
                      onClick={() => setSelectedShipping(option.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              selectedShipping === option.id
                                ? 'border-orange-500'
                                : 'border-gray-600'
                            }`}>
                              {selectedShipping === option.id && (
                                <div className="w-3 h-3 rounded-full bg-orange-500" />
                              )}
                            </div>
                            <div>
                              <h4 className="font-bold text-white">{option.name}</h4>
                              <p className="text-sm text-gray-400">{option.description}</p>
                            </div>
                          </div>
                          <span className="font-bold text-white">
                            {option.price === 0 ? 'Free' : `+$${option.price.toFixed(2)}`}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button 
                onClick={handleAddToCart}
                size="lg"
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold text-lg py-6 transition-all duration-300 transform hover:scale-105"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Proceed to Checkout - ${calculateTotal().toFixed(2)}
              </Button>

              <div className="mt-4 text-center">
                <p className="text-sm text-gray-400 mb-2">
                  Payment: PayPal (contact admin@hhhotsauce.ca)
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Once this batch sells out, it will never be reproduced
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Complete Your Order</DialogTitle>
            <DialogDescription className="text-gray-400">
              Payment via E-Transfer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-800 p-4 rounded-lg">
              <h4 className="font-bold mb-3">Order Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">{productData.name} × {quantity}</span>
                  <span>${(productData.price * quantity).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">{getShippingOption()?.name}</span>
                  <span>{getShippingOption()?.price === 0 ? 'Free' : `$${getShippingOption()?.price.toFixed(2)}`}</span>
                </div>
                {selectedShipping !== 'local' && (
                  <div className="text-xs text-gray-500 italic">
                    {getShippingOption()?.description}
                  </div>
                )}
              </div>
              <div className="border-t border-gray-700 pt-3 mt-3">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-orange-400">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="bg-orange-900/20 border border-orange-600/50 p-4 rounded-lg">
              <h4 className="font-bold mb-3 text-orange-400">Payment Method</h4>
              <div className="mb-4 text-sm text-gray-300">
                <p className="mb-3">
                  ✓ <span className="font-bold text-white">PayPal Payment</span>
                </p>
                <p className="text-yellow-400">
                  Please email or contact <a href="mailto:admin@hhhotsauce.ca" className="font-bold underline hover:text-yellow-300">admin@hhhotsauce.ca</a> for further payment directions.
                </p>
              </div>
              
              <h4 className="font-bold mb-2 text-orange-400 mt-4">Next Steps</h4>
              <ol className="text-sm space-y-2 text-gray-300">
                <li>1. <span className="font-bold text-white">Confirm your order</span> by clicking below</li>
                <li>2. You'll receive order confirmation email</li>
                <li>3. <span className="font-bold text-white">Contact us</span> at admin@hhhotsauce.ca for PayPal payment details</li>
                <li>4. <strong>Preparation time: 1-2 business days</strong></li>
                <li>5. We'll email you when your order ships!</li>
                {selectedShipping === 'canada-express' && (
                  <li className="text-green-400">• Express delivery: 1-3 business days with tracking</li>
                )}
                {selectedShipping === 'canada-standard' && (
                  <li className="text-yellow-400">• Standard delivery: 7-10 business days (no tracking)</li>
                )}
                {selectedShipping === 'usa' && (
                  <li className="text-blue-400">• USA shipping: Standard delivery</li>
                )}
              </ol>
            </div>

            <Button 
              onClick={handleCheckout}
              disabled={submitting}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-6"
            >
              {submitting ? 'Processing...' : 'Confirm Order'}
            </Button>

            <p className="text-xs text-center text-gray-400 mt-2">
              By confirming, you agree to complete payment via E-Transfer or Cash
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Shop;
